import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Star, ShoppingBag, Sparkles, Heart, Shield, Coins, Brain, Flame } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useLang } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

const PURPOSES = [
  { key: 'all', label_en: 'All', label_hi: 'सभी', icon: Sparkles, color: 'from-orange-400 to-red-400' },
  { key: 'wealth', label_en: 'Wealth', label_hi: 'धन', icon: Coins, color: 'from-yellow-400 to-amber-500' },
  { key: 'health', label_en: 'Health', label_hi: 'स्वास्थ्य', icon: Heart, color: 'from-rose-400 to-pink-500' },
  { key: 'love', label_en: 'Love', label_hi: 'प्रेम', icon: Heart, color: 'from-pink-400 to-fuchsia-500' },
  { key: 'protection', label_en: 'Protection', label_hi: 'सुरक्षा', icon: Shield, color: 'from-emerald-400 to-teal-500' },
  { key: 'courage', label_en: 'Courage', label_hi: 'साहस', icon: Flame, color: 'from-red-400 to-orange-500' },
  { key: 'knowledge', label_en: 'Knowledge', label_hi: 'ज्ञान', icon: Brain, color: 'from-indigo-400 to-blue-500' },
];

const CATEGORIES = [
  { key: 'all', label_en: 'All Products', label_hi: 'सभी उत्पाद' },
  { key: 'mala', label_en: 'Mala', label_hi: 'माला' },
  { key: 'yantra', label_en: 'Yantras', label_hi: 'यंत्र' },
  { key: 'idol', label_en: 'Idols', label_hi: 'मूर्ति' },
  { key: 'bracelet', label_en: 'Bracelets', label_hi: 'कड़ा' },
  { key: 'other', label_en: 'Other', label_hi: 'अन्य' },
];

export default function Shop() {
  const { products } = useData();
  const { t, lang } = useLang();
  const { addItem } = useCart();

  const [purpose, setPurpose] = useState('all');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return (products || []).filter((p) => {
      if (purpose !== 'all' && !(p.purposes || []).includes(purpose)) return false;
      if (category !== 'all' && p.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(p.name_en || '').toLowerCase().includes(q) && !(p.name_hi || '').includes(q)) return false;
      }
      return true;
    });
  }, [products, purpose, category, search]);

  const T = {
    title: { en: 'Divine Products', hi: 'दिव्य उत्पाद' },
    subtitle: {
      en: 'Authentic, energised & blessed at Baba Baidyanath Dham. Home delivered across India.',
      hi: 'बाबा बैद्यनाथ धाम में शुद्ध, ऊर्जावान और आशीर्वादित। भारत भर में घर तक डिलीवरी।',
    },
    shopByPurpose: { en: 'Shop by Purpose', hi: 'उद्देश्य से खरीदें' },
    shopByType: { en: 'Shop by Product Type', hi: 'श्रेणी से खरीदें' },
    searchPh: { en: 'Search divine products...', hi: 'दिव्य उत्पाद खोजें...' },
    none: { en: 'No products match your filters', hi: 'आपके फ़िल्टर से कोई उत्पाद मेल नहीं खाता' },
    add: { en: 'Add to Cart', hi: 'टोकरी में जोड़ें' },
    off: { en: 'off', hi: 'छूट' },
    out: { en: 'Out of Stock', hi: 'स्टॉक ख़त्म' },
  };

  return (
    <div className="min-h-screen bg-[#FFF9F0]" data-testid="shop-page">
      {/* Hero */}
      <section className="wine-dark border-b border-nv-gold/20 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-nv-gold-light text-xs md:text-sm tracking-[0.35em] uppercase mb-3">
            ✦ Navavidha Bazaar ✦
          </p>
          <h1 className="text-3xl md:text-5xl font-bold gradient-text-on-dark mb-4">{t(T.title)}</h1>
          <p className="text-nv-cream/85 max-w-2xl mx-auto text-sm md:text-base">{t(T.subtitle)}</p>

          <div className="mt-6 max-w-xl mx-auto relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-nv-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(T.searchPh)}
              className="w-full bg-white/95 border border-nv-gold/30 rounded-full pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-nv-gold"
              data-testid="shop-search"
            />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 space-y-10">
        {/* Shop by Purpose */}
        <section>
          <h2 className="text-xl md:text-2xl font-bold text-nv-wine mb-4">{t(T.shopByPurpose)}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
            {PURPOSES.map((p) => (
              <button
                key={p.key}
                onClick={() => setPurpose(p.key)}
                data-testid={`purpose-${p.key}`}
                className={`group flex flex-col items-center gap-2 p-3 rounded-2xl border transition ${
                  purpose === p.key
                    ? 'border-nv-gold bg-nv-gold/15 shadow-md'
                    : 'border-nv-gold/20 bg-white hover:border-nv-gold/60'
                }`}
              >
                <span
                  className={`w-11 h-11 rounded-full bg-gradient-to-br ${p.color} flex items-center justify-center text-white shadow-md group-hover:scale-105 transition`}
                >
                  <p.icon className="w-5 h-5" />
                </span>
                <span className="text-xs font-semibold text-nv-wine text-center">
                  {lang === 'hi' ? p.label_hi : p.label_en}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Shop by Type */}
        <section>
          <h2 className="text-xl md:text-2xl font-bold text-nv-wine mb-4">{t(T.shopByType)}</h2>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                data-testid={`category-${c.key}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  category === c.key
                    ? 'border-nv-wine bg-nv-wine text-nv-cream'
                    : 'border-nv-gold/30 bg-white text-nv-wine hover:border-nv-gold hover:bg-nv-gold/10'
                }`}
              >
                {lang === 'hi' ? c.label_hi : c.label_en}
              </button>
            ))}
          </div>
        </section>

        {/* Product grid */}
        <section>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-nv-muted" data-testid="shop-empty">
              {t(T.none)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="products-grid">
              {filtered.map((p) => {
                const discount =
                  p.originalPrice && p.originalPrice > p.price
                    ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100)
                    : 0;
                const outOfStock = (p.stock ?? 0) <= 0;
                return (
                  <div
                    key={p.id}
                    className="group bg-white border border-nv-gold/15 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition duration-300 flex flex-col"
                    data-testid={`product-card-${p.id}`}
                  >
                    <Link to={`/shop/${p.id}`} className="relative aspect-square bg-orange-50 overflow-hidden block" data-testid={`product-link-${p.id}`}>
                      <img
                        src={p.image}
                        alt={p.name_en}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                        onError={(e) => {
                          e.currentTarget.src =
                            'https://images.unsplash.com/photo-1518065896235-bd0ab3f2b09d?w=600';
                        }}
                      />
                      {discount > 0 && (
                        <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md">
                          {discount}% {t(T.off)}
                        </span>
                      )}
                      {outOfStock && (
                        <span className="absolute top-2 right-2 bg-stone-800 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                          {t(T.out)}
                        </span>
                      )}
                    </Link>
                    <div className="p-3 md:p-4 flex-1 flex flex-col">
                      <Link to={`/shop/${p.id}`} className="text-sm font-semibold text-nv-wine line-clamp-2 min-h-[2.5rem] hover:text-nv-gold transition">
                        {lang === 'hi' ? p.name_hi || p.name_en : p.name_en}
                      </Link>
                      {p.rating > 0 && (
                        <div className="flex items-center gap-1 mt-1 text-xs">
                          <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                          <span className="font-semibold text-stone-700">{p.rating}</span>
                          <span className="text-stone-400">({p.ratingCount || 0})</span>
                        </div>
                      )}
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-lg font-bold text-nv-wine">₹{p.price}</span>
                        {p.originalPrice > p.price && (
                          <span className="text-xs text-stone-400 line-through">₹{p.originalPrice}</span>
                        )}
                      </div>
                      <button
                        onClick={() => addItem(p)}
                        disabled={outOfStock}
                        data-testid={`add-to-cart-${p.id}`}
                        className={`mt-3 w-full py-2 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5 transition ${
                          outOfStock
                            ? 'bg-stone-200 text-stone-500 cursor-not-allowed'
                            : 'bg-yellow-400 hover:bg-yellow-500 text-nv-wine shadow-sm hover:shadow-md'
                        }`}
                      >
                        <ShoppingBag className="w-4 h-4" />
                        {t(T.add)}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
